import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, FileText, AlertCircle } from "lucide-react";
import {
  getCoachingAthleteLactateSuggestion,
  updateCoachingAthlete,
} from "../../lib/api";
import { toast } from "sonner";

/**
 * LactateProfile — displays the latest lactate test linked to a coaching
 * athlete (matched by email). When present, the planner already prefers
 * lactate-derived training paces over VDOT estimates, so this panel gives the
 * coach a one-glance confirmation of what paces are flowing into the program.
 *
 * Props:
 *  - athlete:        coaching athlete document (for email/name)
 *  - lactate:        latest Test document (or null/undefined when no match)
 *  - onAthleteChange: optional callback when this panel saves a profile change
 *                    (e.g. reconciling a mismatched email).
 */
export default function LactateProfile({ athlete, lactate, onAthleteChange }) {
  const hasEmail = !!(athlete?.email || "").trim();
  const [suggestion, setSuggestion] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  // When the email-based fetch returned nothing, try a name-based lookup so
  // we can surface a "did you mean this email?" hint to the coach. This is
  // the most common cause of a missing link: emails differ on each side.
  useEffect(() => {
    if (lactate || !athlete?.id) {
      setSuggestion(null);
      return;
    }
    let alive = true;
    getCoachingAthleteLactateSuggestion(athlete.id)
      .then((s) => {
        if (!alive) return;
        // Only surface as a suggestion when the lactate side has an email
        // that differs from the coaching side — that's the actionable case.
        if (s && s.athlete_email && s.athlete_email !== athlete.email) {
          setSuggestion(s);
        } else {
          setSuggestion(null);
        }
      })
      .catch(() => alive && setSuggestion(null));
    return () => {
      alive = false;
    };
  }, [athlete?.id, athlete?.email, lactate]);

  const useSuggestedEmail = async () => {
    if (!suggestion?.athlete_email) return;
    setReconciling(true);
    try {
      const updated = await updateCoachingAthlete(athlete.id, {
        email: suggestion.athlete_email,
      });
      toast.success(`Coaching email updated to ${suggestion.athlete_email}`);
      onAthleteChange?.(updated);
    } catch {
      toast.error("Couldn't update — try again");
    } finally {
      setReconciling(false);
    }
  };

  // No email → no possible link. Hide entirely; the email CTA elsewhere on
  // the page already prompts the coach to add one.
  if (!hasEmail) return null;

  // Has email but no test found — show a small explanatory card so the coach
  // knows the auto-link tried and didn't find anything (vs. silently absent).
  if (!lactate) {
    return (
      <div
        className="border border-[var(--tir-border)] bg-white p-6"
        data-testid="lactate-profile-empty"
      >
        <div className="flex items-start gap-3">
          <Activity size={18} className="text-[var(--tir-navy)] mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-serif-display text-xl text-[var(--tir-ink)] leading-tight">
              Lactate test
            </div>
            <p className="text-sm text-[var(--tir-muted)] mt-2 leading-relaxed max-w-xl">
              No lactate test on file for{" "}
              <span className="font-mono-tir text-[var(--tir-ink)]">
                {athlete.email}
              </span>
              .
            </p>

            {suggestion ? (
              <div
                className="mt-4 border-l-2 border-[var(--tir-burgundy)] bg-[#fdf6f4] pl-4 pr-4 py-3 max-w-2xl"
                data-testid="lactate-profile-suggestion"
              >
                <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-burgundy)] mb-1">
                  <AlertCircle size={12} /> Possible match found
                </div>
                <p className="text-sm text-[var(--tir-ink)] leading-relaxed">
                  A lactate test for{" "}
                  <span className="font-semibold">
                    {suggestion.athlete_name}
                  </span>{" "}
                  is on file under email{" "}
                  <span className="font-mono-tir">
                    {suggestion.athlete_email}
                  </span>
                  {suggestion.test_date ? (
                    <> (tested {suggestion.test_date})</>
                  ) : null}
                  . Your coaching profile uses a different email, so the
                  planner isn't picking it up.
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <button
                    type="button"
                    onClick={useSuggestedEmail}
                    disabled={reconciling}
                    data-testid="lactate-profile-use-suggested-email-btn"
                    className="inline-flex items-center gap-1.5 bg-[var(--tir-burgundy)] text-white px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
                  >
                    {reconciling ? "Updating…" : "Use this email"}
                  </button>
                  <Link
                    to={`/athletes/${suggestion.athlete_id}/tests/${suggestion.test_id}`}
                    data-testid="lactate-profile-suggestion-view-link"
                    className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
                  >
                    View the test →
                  </Link>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--tir-muted)] mt-2 max-w-xl">
                When you record a test in the Lactate hub under this exact
                email, the planner will switch from VDOT estimates to
                lactate-derived training paces automatically.
              </p>
            )}

            <Link
              to="/lactate"
              data-testid="lactate-profile-hub-link"
              className="inline-flex items-center gap-1.5 mt-3 text-xs tracking-[0.2em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
            >
              Open Lactate hub →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const lt1 = lactate.lt1 || {};
  const lt2 = lactate.lt2 || {};
  const zones = Array.isArray(lactate.zones) ? lactate.zones : [];
  const rows = Array.isArray(lactate.rows) ? lactate.rows : [];

  // Best-effort name match warning: if the lactate test was for a different
  // athlete (only email matched), surface that so the coach can investigate.
  const sameName =
    !lactate.athlete_name ||
    !athlete?.name ||
    lactate.athlete_name.trim().toLowerCase() ===
      athlete.name.trim().toLowerCase();

  return (
    <div
      className="border border-[var(--tir-border)] bg-white p-6"
      data-testid="lactate-profile-card"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Activity size={18} className="text-[var(--tir-navy)] mt-1 shrink-0" />
          <div>
            <div className="font-serif-display text-xl text-[var(--tir-ink)] leading-tight">
              Lactate test
              <span className="ml-2 inline-block px-1.5 py-0.5 align-middle bg-[#e8eef2] text-[var(--tir-navy)] text-[9px] tracking-[0.22em] uppercase font-semibold">
                Active
              </span>
            </div>
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] mt-1">
              {lactate.test_date ? `Tested ${lactate.test_date}` : "Latest test on file"}
              {lactate.protocol ? ` · ${lactate.protocol}` : ""}
            </div>
            {!sameName && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)]">
                <AlertCircle size={12} /> Test is on file for{" "}
                <span className="font-mono-tir normal-case tracking-normal">
                  {lactate.athlete_name}
                </span>{" "}
                — same email, different name
              </div>
            )}
          </div>
        </div>
        {lactate.athlete_id && lactate.id && (
          <Link
            to={`/athletes/${lactate.athlete_id}/tests/${lactate.id}`}
            data-testid="lactate-profile-view-link"
            className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
            title="Open the full lactate test (chart + zones + PDF)"
          >
            <FileText size={13} /> View full test
          </Link>
        )}
      </div>

      <p className="text-sm text-[var(--tir-muted)] leading-relaxed max-w-xl mb-5">
        The planner uses these zones to prescribe training paces for this
        athlete instead of VDOT estimates.
      </p>

      {/* LT1 / LT2 thresholds at-a-glance */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 max-w-md mb-6">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
            LT1 · Aerobic threshold
          </div>
          <div className="font-mono-tir text-lg mt-1 text-[var(--tir-ink)]">
            {lt1.pace || "—"}
            {lt1.hr ? (
              <span className="text-[var(--tir-muted)] text-sm ml-2">
                · {Math.round(lt1.hr)} bpm
              </span>
            ) : null}
          </div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
            LT2 · Anaerobic threshold
          </div>
          <div className="font-mono-tir text-lg mt-1 text-[var(--tir-ink)]">
            {lt2.pace || "—"}
            {lt2.hr ? (
              <span className="text-[var(--tir-muted)] text-sm ml-2">
                · {Math.round(lt2.hr)} bpm
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Training zones from the test */}
      {zones.length > 0 && (
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
            Training zones
          </div>
          <div className="border-t border-[var(--tir-border)]">
            {zones.map((z, i) => (
              <div
                key={`${z.zone_label}-${i}`}
                className="grid grid-cols-12 gap-3 py-2 border-b border-[var(--tir-border)] text-sm items-baseline"
              >
                <div className="col-span-2 font-mono-tir text-[var(--tir-burgundy)] font-semibold text-xs tracking-[0.1em] uppercase">
                  {z.zone_label}
                </div>
                <div className="col-span-3 text-[var(--tir-ink)]">{z.name}</div>
                <div className="col-span-4 font-mono-tir text-[var(--tir-ink)]">
                  {z.pace_range || "—"}
                  <span className="text-[var(--tir-muted)] text-xs ml-1">/km</span>
                </div>
                <div className="col-span-3 font-mono-tir text-[var(--tir-muted)] text-xs">
                  {z.hr_range ? `${z.hr_range} bpm` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {zones.length === 0 && rows.length > 0 && (
        <div className="text-xs italic text-[var(--tir-muted)] border-l-2 border-[var(--tir-border)] pl-3 py-1">
          This test has raw data points but no zones defined yet. Open the full
          test to build zones — they're what drives the planner's pace
          prescriptions.
        </div>
      )}
    </div>
  );
}
